/**
 * Keyset ("cursor") pagination.
 *
 * Offset pagination is wrong for an infinite gallery in two ways: it gets
 * slower the further you scroll, and when new photos arrive mid-scroll every
 * subsequent page shifts, so the user sees duplicates and misses others.
 * A keyset cursor is stable and O(log n) regardless of depth.
 *
 * The cursor is opaque to the client but is NOT a security boundary — it
 * carries only a sort key and an id. The `group_id` filter is always applied
 * server-side from the session, never from the cursor, so tampering with a
 * cursor cannot reach another group's rows.
 */

export const GALLERY_SORTS = [
  "taken_desc",
  "taken_asc",
  "uploaded_desc",
  "uploaded_asc",
  "name_asc",
  "name_desc",
  "size_desc",
] as const;

export type GallerySort = (typeof GALLERY_SORTS)[number];

export const DEFAULT_GALLERY_SORT: GallerySort = "taken_desc";

export interface Cursor {
  /** Primary sort value: an ISO timestamp, a name, or a byte count as string. */
  k: string;
  /** Tie-breaker. Always present, so the ordering is a total order. */
  id: string;
}

export function encodeCursor(cursor: Cursor): string {
  const json = JSON.stringify([cursor.k, cursor.id]);
  return toBase64Url(json);
}

export function decodeCursor(encoded: string | null | undefined): Cursor | null {
  if (!encoded) return null;
  try {
    const parsed: unknown = JSON.parse(fromBase64Url(encoded));
    if (!Array.isArray(parsed) || parsed.length !== 2) return null;
    const [k, id] = parsed;
    if (typeof k !== "string" || typeof id !== "string") return null;
    // Bound the inputs so a hostile cursor cannot become a large query literal.
    if (k.length > 256 || id.length > 64) return null;
    return { k, id };
  } catch {
    return null;
  }
}

/** A cursor whose sort key has been resolved to a timestamp. */
export interface TimestampCursor {
  k: Date;
  id: string;
}

/**
 * Decode a cursor whose `k` is expected to be a timestamp.
 *
 * `decodeCursor` cannot make this check itself — `k` is a name or a byte count
 * for half the sorts, and only the caller knows which. But a caller that does
 * know and skips the check pays for it: `new Date("zzz")` is an Invalid Date,
 * the driver throws `RangeError` while binding it, and a hand-crafted cursor
 * becomes a 500 logged as a server fault. A timestamp that does not parse is
 * treated exactly as malformed JSON already is — as no cursor at all, so the
 * caller falls back to the first page.
 */
export function decodeTimestampCursor(
  encoded: string | null | undefined,
): TimestampCursor | null {
  const cursor = decodeCursor(encoded);
  if (!cursor) return null;
  const k = new Date(cursor.k);
  if (Number.isNaN(k.getTime())) return null;
  return { k, id: cursor.id };
}

export function sortDirection(sort: GallerySort): "asc" | "desc" {
  return sort.endsWith("_asc") ? "asc" : "desc";
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
  /** Only present on the first page; expensive to compute, so it is optional. */
  totalCount?: number;
}

export const DEFAULT_PAGE_SIZE = 100;
export const MAX_PAGE_SIZE = 500;

export function clampPageSize(requested: number | undefined): number {
  if (!requested || !Number.isFinite(requested)) return DEFAULT_PAGE_SIZE;
  return Math.min(MAX_PAGE_SIZE, Math.max(1, Math.floor(requested)));
}

function toBase64Url(input: string): string {
  const base64 =
    typeof btoa === "function"
      ? btoa(unescape(encodeURIComponent(input)))
      : Buffer.from(input, "utf8").toString("base64");
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(input: string): string {
  const base64 = input.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  return typeof atob === "function"
    ? decodeURIComponent(escape(atob(padded)))
    : Buffer.from(padded, "base64").toString("utf8");
}
